import { gql } from '@apollo/client';

// ═══════════════════════════════════════════════════════════════════
// Fragments V4 post-refactor 2026-04-22
// - SR: sin scheduledStartDate/actualStartDate/actualEndDate (→Order.openingDate/
//   registerInitDate/registerEndDate), sin description (→Order.description),
//   sin startedBy/endedBy (→Order.user), sin createdAt/updatedAt (no estándar),
//   sin segments[] (relación vive en Order parent hierarchy).
//   operators → users (patrón Action).
// - RouteSegment: sin segmentNumber (reemplazado por indexType), sin description
//   (→Order.description), sin startedBy/endedBy (→Order.user), sin surveillanceRouteId
//   (via Order.parentExpeditionOrderIDs).
// ═══════════════════════════════════════════════════════════════════

export const ROUTE_SEGMENT_FRAGMENT = gql`
  fragment RouteSegmentFragment on RouteSegment {
    id
    indexExpedition
    indexType
    state
    direction
    directionName
    lane
    # Multi-carretera (2026-08-15): en qué carretera del recorrido EMPIEZA el
    # tramo. Nulo = la primera, que es la que tenía implícita.
    roadId
    # Por qué carreteras pasó, en orden (2026-08-17). Vacío = una sola, la del
    # campo de arriba. Un tramo no se parte al cruzar de vía —terminaría sin
    # causa, que es un evento que no ocurrió— así que registra sus trozos.
    # (Sin acentos graves aquí: cerrarían el template literal de gql.)
    roadStretches {
      roadId
      pkInitKm
      pkInitMeter
      pkEndKm
      pkEndMeter
    }
    pkInitKm
    pkInitMeter
    pkEndKm
    pkEndMeter
    initLatitude
    initLongitude
    endLatitude
    endLongitude
    startDate
    endDate
    pauseCause
    pauseCauseDetail
    pendingExternalLink
    pendingActionLink
    creationDate
    registerInitDate
    orderIDs
    # 2026-08-11: recorrido padre resuelto EN LOTE por el backend. Sin esto la
    # columna "Nombre del recorrido" del modal «Vincular tramo» solo podía
    # pintar el código Su.NNNN derivado del ROID, porque la relación vive en
    # Order.parentExpeditionOrderIDs y resolverla por fila sería un N+1.
    surveillanceRoute {
      id
      name
      indexExpedition
    }
  }
`;

// D3 (2026-07-23): fragment del DETALLE — extiende el base con los vínculos del
// tramo (Act. básica / OT sala). Los campos `linked*` son @ResolveField batched
// en backend. DELIBERADAMENTE separado del base: el polling del timer
// (`GET_ACTIVE_ROUTE_SEGMENT`, cada 30s) y los modales usan el base y NO pagan
// el coste de resolver los vínculos.
export const ROUTE_SEGMENT_DETAIL_FRAGMENT = gql`
  ${ROUTE_SEGMENT_FRAGMENT}
  fragment RouteSegmentDetailFragment on RouteSegment {
    ...RouteSegmentFragment
    linkedBasicActionOrder {
      id
      roid
      type
      state
      actionId
    }
    linkedExternalOrder {
      id
      roid
      type
      state
    }
    # «Registrar parada» (2026-10-06): rastro de la partición.
    stopSplit {
      operationId
      role
      byUserId
      byName
      at
      undoable
    }
    # «Transferir tramo» (2026-10-07): de dónde vino.
    transfer {
      fromRouteId
      fromRouteRoid
      fromRoid
      at
      byUserId
      byName
    }
    # «Tramos revisados» (2026-10-27): solo en el detalle, el polling del timer no lo paga.
    myReview {
      reviewedAt
      stale
    }
  }
`;

export const ROUTE_GPS_POINT_FRAGMENT = gql`
  fragment RouteGpsPointFragment on RouteGpsPoint {
    id
    latitude
    longitude
    timestamp
    speed
    accuracy
  }
`;

export const SURVEILLANCE_ROUTE_FRAGMENT = gql`
  fragment SurveillanceRouteFragment on SurveillanceRoute {
    id
    indexExpedition
    indexType
    state
    name
    pkInitKm
    pkInitMeter
    pkEndKm
    pkEndMeter
    creationDate
    registerInitDate
    orderIDs
    concessionIDs
    roadIDs
    # Multi-carretera (2026-08-17): la tabla del listado y el Excel pintaban el
    # rango escalar, que es el de la PRIMERA carretera, en un recorrido que
    # cubre varias. Con estos, cada celda apila un valor por carretera.
    roadRanges {
      roadId
      pkInitKm
      pkInitMeter
      pkEndKm
      pkEndMeter
    }
    teamIDs
    capitolIDs
    subCapitolIDs
    predefinedOrderIDs
    userId
    timerOwnerId
    userIDs
    vehicleOccupantIDs
  }
`;

export const SURVEILLANCE_ROUTE_FULL_FRAGMENT = gql`
  ${ROUTE_SEGMENT_FRAGMENT}
  ${ROUTE_GPS_POINT_FRAGMENT}
  fragment SurveillanceRouteFullFragment on SurveillanceRoute {
    id
    indexExpedition
    indexType
    state
    name
    pkInitKm
    pkInitMeter
    pkEndKm
    pkEndMeter
    creationDate
    registerInitDate
    forceClosedById
    forceClosedAt
    orderIDs
    concessionIDs
    roadIDs
    roadRanges {
      roadId
      pkInitKm
      pkInitMeter
      pkEndKm
      pkEndMeter
    }
    teamIDs
    capitolIDs
    subCapitolIDs
    predefinedOrderIDs
    userId
    timerOwnerId
    userIDs
    vehicleOccupantIDs
    segments {
      ...RouteSegmentFragment
    }
    gpsPoints(simplified: true) {
      ...RouteGpsPointFragment
    }
    # Cross-link 3 (2026-09-22): OTs vinculadas al recorrido con procedencia y
    # los tramos de ESTE recorrido ligados a cada una. Resuelto en lote en el
    # backend; solo el detalle lo pide.
    linkedExternalOrders {
      order {
        id
        roid
        type
        state
        actionId
      }
      source
      linkedAt
      viaOrderId
      segments {
        id
        indexType
        pauseCause
        endDate
      }
    }
  }
`;

// Informe RV (2026-07-27): fragment DEDICADO del informe PDF. Igual que el
// Full salvo dos diferencias deliberadas:
//   (a) SIN `gpsPoints` — el informe no pinta el track y son ~51 kB por
//       recorrido (medido en E3); con varios recorridos en un ZIP se nota.
//   (b) tramos con el fragment DETALLE, que es el único que trae los
//       vínculos (`linkedBasicActionOrder`/`linkedExternalOrder`) que el
//       informe pinta como columnas "Act. básica" / "OT sala".
// Mismo criterio que `SURVEILLANCE_ROUTE_EDIT_FRAGMENT`: selección explícita.
// Si el informe necesita un campo nuevo, añádelo aquí — NO vuelvas al Full
// "por comodidad", eso reintroduce el peso que este fragment evita.
export const SURVEILLANCE_ROUTE_REPORT_FRAGMENT = gql`
  ${ROUTE_SEGMENT_DETAIL_FRAGMENT}
  fragment SurveillanceRouteReportFragment on SurveillanceRoute {
    id
    indexExpedition
    indexType
    state
    name
    pkInitKm
    pkInitMeter
    pkEndKm
    pkEndMeter
    creationDate
    registerInitDate
    forceClosedById
    forceClosedAt
    orderIDs
    concessionIDs
    roadIDs
    # Multi-carretera (2026-08-17): la ficha del informe pintaba UN rango de PK
    # (el escalar, que apunta a la primera carretera) bajo un titulo que hablaba
    # del recorrido entero. Con estos, cada rango va pegado a su carretera.
    roadRanges {
      roadId
      pkInitKm
      pkInitMeter
      pkEndKm
      pkEndMeter
    }
    teamIDs
    capitolIDs
    subCapitolIDs
    predefinedOrderIDs
    userId
    userIDs
    vehicleOccupantIDs
    segments {
      ...RouteSegmentDetailFragment
    }
    # Cross-link 3 (2026-09-22): OTs vinculadas al recorrido — el informe pinta
    # su ficha, la primera comunicación y los trabajos del equipo en el turno.
    linkedExternalOrders {
      order {
        id
        roid
        type
        state
        actionId
      }
      source
      linkedAt
      viaOrderId
      workOrderIds
      segments {
        id
        indexType
        pauseCause
        endDate
      }
    }
  }
`;

// Perf E3 (2026-07-20): fragment DEDICADO del loader de `EditRoute` — medido
// en vivo, `SurveillanceRouteFullFragment` pesa 54,5kB de los que 51,4kB son
// los 259 `gpsPoints(simplified: true)` que el wizard de edición JAMÁS lee
// (no pinta mapa/track). Selección MÍNIMA verificada contra
// `EditRoute/loader.ts`: los escalares que precarga (Ubicació + PKs +
// relaciones ×1) + `segments { id }` SOLO para el conteo `hasSegments`
// (aviso "cambiar de carretera con tramos ya registrados" — no necesita
// coords/fechas/pausas de cada tramo, solo saber si `length > 0`).
// Deliberadamente SIN `gpsPoints` y SIN `...RouteSegmentFragment` completo.
// Si el wizard necesita un campo nuevo, añádelo aquí explícitamente — NO
// cambies el loader a `SurveillanceRouteFullFragment` "por comodidad", eso
// reintroduce el peso que este fragment existe para evitar.
// Perf detalle (2026-07-21, hermano de E3): fragment DEDICADO de los DOS
// consumidores de `surveillanceRouteByOrderId` en `gql/selectors/orders.ts`
// — medido en vivo, la query con `SurveillanceRouteFullFragment` pesaba
// 61,4kB/1,9s de los que ~51kB eran 259 `gpsPoints(simplified: true)` +
// tramos completos que NINGÚN call-site lee. Selección MÍNIMA verificada
// contra ambos consumidores:
// - `resolveLinkedRv` (~:100): lee `id` + `buildRoidFallback(sr)`
//   (`indexExpedition`/`indexType`).
// - card padre del RV en el detalle/child-orders (~:580): lee `id`, `name`,
//   `roadIDs`, `concessionIDs`, `pkInit*`/`pkEnd*`, `teamIDs` (sus reads de
//   `lane`/`direction`/`margin`/`track` son campos que el type
//   SurveillanceRoute NO tiene — hoy ya llegaban undefined→'' con el Full).
// Deliberadamente SIN `gpsPoints` y SIN `segments`. Si un consumidor
// necesita un campo nuevo, añádelo aquí explícitamente — NO vuelvas al
// `SurveillanceRouteFullFragment` "por comodidad".
export const SURVEILLANCE_ROUTE_CARD_FRAGMENT = gql`
  fragment SurveillanceRouteCardFragment on SurveillanceRoute {
    id
    indexExpedition
    indexType
    name
    pkInitKm
    pkInitMeter
    pkEndKm
    pkEndMeter
    concessionIDs
    roadIDs
    # 🔴 Sin esto la tarjeta de la OT enseñaba, para cada vía, el rango
    # DECLARADO de la carretera en vez del que se eligió para el recorrido: un
    # PK fin corregido a 236+600 seguía leyéndose como 237+100. La cabecera de
    # Vista previa sí lo pedía, así que las dos pantallas contaban cosas
    # distintas del mismo recorrido.
    roadRanges {
      roadId
      pkInitKm
      pkInitMeter
      pkEndKm
      pkEndMeter
    }
    teamIDs
  }
`;

export const SURVEILLANCE_ROUTE_EDIT_FRAGMENT = gql`
  fragment SurveillanceRouteEditFragment on SurveillanceRoute {
    id
    state
    name
    pkInitKm
    pkInitMeter
    pkEndKm
    pkEndMeter
    orderIDs
    concessionIDs
    roadIDs
    roadRanges {
      roadId
      pkInitKm
      pkInitMeter
      pkEndKm
      pkEndMeter
    }
    teamIDs
    # Dueno del cronometro (2026-09-23): lo lee la guarda del asistente para
    # que una tablet de la OTRA patrulla no edite entrando por URL. Sin el, la
    # guarda solo podria mirar el equipo.
    timerOwnerId
    userIDs
    vehicleOccupantIDs
    segments {
      id
    }
  }
`;
